import { validateAppointmentBookingReceipt, type AppointmentBookingReceiptV2 } from "@/app/booking/core/appointmentBookingReceipt";
import { samePackageContractData } from "@/app/commerce/packages/packagePurchaseContractData";
import { captureCustomerOwnedSessionStorageMutationProof, customerOwnedSessionStorageMutationProofIsCurrent } from "@/app/booking/core/customerOwnedSessionStorageMutationCoordinator";
import type { AppointmentActivityItem } from "./activityContracts";
import { captureActivityPlainData } from "./activityPlainDataCapture";
import { isActivityIdentifier, isValidActivityEpochMs, validateActivityItem } from "./activityValidation";

/** An injected provider's acceptance ledger, never the browser receipt store. */
export interface FrontendSingleAppointmentAcceptedSourceV1 {
  businessId: string;
  receipt: AppointmentBookingReceiptV2;
}

export interface FrontendSingleAppointmentPublicationV1 {
  schemaVersion: 1;
  kind: "single-appointment-current-publication";
  publicationRevision: string;
  customerAccountId: string;
  providerBookingId: string;
  providerReceiptId: string;
  idempotencyKey: string;
  sourceAppointmentDraftFingerprint: string;
  publishedAtEpochMs: number;
  expiresAtEpochMs: number;
  item: AppointmentActivityItem;
  lineMappings: readonly {
    sourceParticipantId: string;
    sourceLineId: string;
    activityLineId: string;
  }[];
}

export type FrontendSingleAppointmentPublicationRead =
  | { status: "ready"; customerAccountId: string; generation: number; publications: readonly FrontendSingleAppointmentPublicationV1[] }
  | { status: "unavailable" };

export interface FrontendSingleAppointmentPublicationSource {
  read(customerAccountId: string, nowEpochMs: number): FrontendSingleAppointmentPublicationRead;
  subscribe(listener: () => void): () => void;
}

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
function exactKeys(value: Record<string, unknown>, keys: readonly string[]) {
  return Object.keys(value).length === keys.length && keys.every(key => Object.hasOwn(value, key));
}
function capture(value: unknown) {
  const result = captureActivityPlainData(value);
  return result.valid ? result.value : null;
}
function freeze<T>(value: T): T {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
  return value;
}
function sameAddOns(
  source: readonly { groupId: string; addOnId: string; quantity: number }[],
  current: readonly { groupId: string; addOnId: string; quantity: number }[],
) {
  return source.length === current.length && source.every((row, index) =>
    row.groupId === current[index]?.groupId && row.addOnId === current[index]?.addOnId && row.quantity === current[index]?.quantity,
  );
}

/** Current metadata is independently authored. Acceptance only proves its join. */
export function validateFrontendSingleAppointmentPublicationV1(
  input: unknown,
  acceptedInput: unknown,
  customerAccountId: string,
  nowEpochMs: number,
): FrontendSingleAppointmentPublicationV1 | null {
  try {
    const value = capture(input);
    const accepted = capture(acceptedInput);
    if (!isActivityIdentifier(customerAccountId) || !isValidActivityEpochMs(nowEpochMs) ||
      !record(value) || !exactKeys(value, ["schemaVersion", "kind", "publicationRevision", "customerAccountId", "providerBookingId", "providerReceiptId", "idempotencyKey", "sourceAppointmentDraftFingerprint", "publishedAtEpochMs", "expiresAtEpochMs", "item", "lineMappings"]) ||
      !record(accepted) || !exactKeys(accepted, ["businessId", "receipt"]) || !isActivityIdentifier(accepted.businessId)) return null;
    const receiptResult = validateAppointmentBookingReceipt(accepted.receipt);
    const itemResult = validateActivityItem(value.item);
    if (!receiptResult.valid || !itemResult.valid || itemResult.item.activityType !== "appointment") return null;
    const receipt = receiptResult.receipt;
    const item = itemResult.item;
    const draft = receipt.reviewDraft.intakeDraft.schedulingDraft.appointmentDraft;
    if (value.schemaVersion !== 1 || value.kind !== "single-appointment-current-publication" ||
      !isActivityIdentifier(value.publicationRevision) || value.customerAccountId !== customerAccountId ||
      receipt.customerAccountId !== customerAccountId || item.ownership.customerAccountId !== customerAccountId ||
      draft.mode !== "individual" || draft.participants.length !== 1 || item.party.kind !== "individual" ||
      value.providerBookingId !== receipt.providerBookingId || value.providerBookingId !== item.activityId ||
      item.appointmentReference !== receipt.providerBookingId || value.providerReceiptId !== receipt.providerReceiptId ||
      value.idempotencyKey !== receipt.idempotencyKey || value.sourceAppointmentDraftFingerprint !== receipt.sourceAppointmentDraftFingerprint ||
      item.business.businessId !== accepted.businessId || item.location.locationId !== draft.locationId ||
      !isValidActivityEpochMs(value.publishedAtEpochMs) || !isValidActivityEpochMs(value.expiresAtEpochMs) ||
      value.publishedAtEpochMs < receipt.createdAtEpochMs || value.publishedAtEpochMs > nowEpochMs ||
      value.expiresAtEpochMs <= nowEpochMs || value.expiresAtEpochMs <= value.publishedAtEpochMs ||
      item.freshness.status !== "fresh" || item.freshness.observedAtEpochMs > nowEpochMs || item.freshness.staleAtEpochMs <= nowEpochMs ||
      item.freshness.observedAtEpochMs < value.publishedAtEpochMs ||
      item.revision.generatedAtEpochMs < receipt.createdAtEpochMs || item.revision.generatedAtEpochMs > value.publishedAtEpochMs ||
      item.freshness.staleAtEpochMs > value.expiresAtEpochMs ||
      item.ownership.relationship !== "organizer" || item.membershipRedemption || item.packageRedemption || item.giftCardRedemption ||
      !Array.isArray(value.lineMappings)) return null;
    const review = receipt.reviewDraft;
    const selectedBenefit = review.benefitEligibility.eligibleBenefits.find(row => row.benefitId === review.appliedBenefitId);
    if (selectedBenefit?.kind === "membership" || selectedBenefit?.kind === "package") return null;
    const expectedAdjustments = [
      ...(review.pricingAdjustments?.automaticDeals ?? []).map(row => ({ adjustmentId: row.dealId, kind: "discount", amountMinor: row.discountAmountMinor })),
      ...(review.pricingAdjustments?.promotionCodeClaim ? [{ adjustmentId: review.pricingAdjustments.promotionCodeClaim.claimId, kind: "promotion", amountMinor: review.pricingAdjustments.promotionCodeClaim.discountAmountMinor }] : []),
      ...(selectedBenefit ? [{ adjustmentId: selectedBenefit.benefitId, kind: selectedBenefit.kind === "promotion" ? "promotion" : "discount", amountMinor: selectedBenefit.discountAmountMinor }] : []),
    ];
    if (item.pricing.adjustments.length !== expectedAdjustments.length || expectedAdjustments.some((expected, index) => {
      const adjustment = item.pricing.adjustments[index];
      return !adjustment || adjustment.adjustmentId !== expected.adjustmentId || adjustment.kind !== expected.kind || adjustment.amount.amountMinor !== expected.amountMinor;
    })) return null;
    const quote = review.quote;
    if (item.pricing.subtotal.amountMinor !== quote.subtotalAmountMinor || item.pricing.total.amountMinor !== quote.totalAmountMinor ||
      item.pricing.subtotal.currency !== quote.currency || item.pricing.tax.amountMinor !== 0 || item.pricing.tip.amountMinor !== 0 ||
      item.pricing.fees.length !== 0 || item.pricing.adjustments.reduce((sum, row) => sum + row.amount.amountMinor, 0) !== quote.discountAmountMinor ||
      item.pricing.adjustments.some(row => row.kind === "membership-benefit" || row.kind === "package-benefit")) return null;
    const participant = draft.participants[0]!;
    if (value.lineMappings.length !== participant.serviceLines.length || item.lines.length !== participant.serviceLines.length) return null;
    for (let index = 0; index < participant.serviceLines.length; index += 1) {
      const mapping = value.lineMappings[index];
      const source = participant.serviceLines[index]!;
      const line = item.lines[index]!;
      if (!record(mapping) || !exactKeys(mapping, ["sourceParticipantId", "sourceLineId", "activityLineId"]) ||
        mapping.sourceParticipantId !== participant.participantId || mapping.sourceLineId !== source.lineId || mapping.activityLineId !== line.lineId ||
        source.selection.kind !== line.kind) return null;
      const quoted = receipt.reviewDraft.intakeDraft.schedulingDraft.selectedSlot.quote.lineItems[index];
      const addOns = line.kind === "service" ? line.addOns : line.children.flatMap(child => child.addOns);
      if (!quoted || quoted.participantId !== participant.participantId || quoted.lineId !== source.lineId ||
        line.price.currency !== quote.currency || line.price.amountMinor + addOns.reduce((sum, row) => sum + (row.price?.amountMinor ?? 0) * row.quantity, 0) !== quoted.amountMinor) return null;
      if (source.selection.kind === "service" && line.kind === "service") {
        if (source.selection.serviceId !== line.serviceId || source.selection.variantId !== line.variantId ||
          !sameAddOns(source.selection.addOns, line.addOns)) return null;
      } else if (source.selection.kind === "bundle" && line.kind === "bundle") {
        if (source.selection.serviceId !== line.bundleId || source.selection.scheduleType !== line.schedule ||
          source.selection.bundleItems.length !== line.children.length) return null;
        for (let childIndex = 0; childIndex < line.children.length; childIndex += 1) {
          const child = line.children[childIndex]!;
          const sourceChild = source.selection.bundleItems[childIndex]!;
          if (sourceChild.variantId !== (child.variantV1?.variantId ?? null) || child.childId !== sourceChild.bundleItemId || child.serviceId !== sourceChild.serviceId ||
            !sameAddOns(sourceChild.addOns, child.addOns)) return null;
        }
      }
    }
    return freeze({ ...value, item } as unknown as FrontendSingleAppointmentPublicationV1);
  } catch { return null; }
}

const sourceVersions = new WeakMap<FrontendSingleAppointmentPublicationSource, number>();
const readVersions = new WeakMap<object, { source: FrontendSingleAppointmentPublicationSource; version: number }>();
/** Callback-free final guard, including a change after the last composed read. */
export function frontendSingleAppointmentPublicationReadIsCurrent(source: FrontendSingleAppointmentPublicationSource, read: FrontendSingleAppointmentPublicationRead): boolean {
  const proof = readVersions.get(read);
  return !!proof && proof.source === source && sourceVersions.get(source) === proof.version;
}

const registeredSources = new WeakMap<FrontendSingleAppointmentPublicationSource, FrontendSingleAppointmentPublicationSource["read"]>();

/** Only the explicit controller can attest a detached read against its ledger.
 * A shaped object or copied publication is not provider acceptance authority. */
export function readFrontendSingleAppointmentPublicationsV1(source: FrontendSingleAppointmentPublicationSource, customer: string, nowEpochMs: number): FrontendSingleAppointmentPublicationRead {
  try { return registeredSources.get(source)?.(customer, nowEpochMs) ?? { status: "unavailable" }; }
  catch { return { status: "unavailable" }; }
}

/** Explicit memory-only simulator controller. It cannot submit or create Forms. */
export function createFrontendSingleAppointmentPublicationSourceV1(options: {
  readAccepted(customerAccountId: string, idempotencyKey: string): unknown;
  now?: () => number;
  isCurrentOwner?: (customerAccountId: string) => boolean;
}) {
  const rows = new Map<string, { publication: FrontendSingleAppointmentPublicationV1; accepted: unknown }>();
  const generations = new Map<string, number>();
  const usedRevisions = new Set<string>();
  // Retain identity ownership through withdrawal; absence is no transfer.
  const acceptedIdentities = new Map<string, { customer: string; accepted: unknown }>();
  const listeners = new Set<() => void>();
  let mutationVersion = 0;
  let writing = false;
  let reading = false;
  const now = options.now ?? Date.now;
  const keyFor = (customer: string, booking: string) => JSON.stringify([customer, booking]);
  const changed = (customer: string) => {
    mutationVersion += 1;
    sourceVersions.set(source, mutationVersion);
    generations.set(customer, (generations.get(customer) ?? 0) + 1);
    for (const listener of [...listeners]) { try { listener(); } catch { /* A committed publication remains committed. */ } }
  };
  const currentOwner = (customer: string) => options.isCurrentOwner?.(customer) ?? true;
  const read: FrontendSingleAppointmentPublicationSource["read"] = (customerAccountId, nowEpochMs) => {
    if (reading) return { status: "unavailable" };
    reading = true;
    const mutation = mutationVersion;
    const proof = captureCustomerOwnedSessionStorageMutationProof();
    try {
      if (!isActivityIdentifier(customerAccountId) || !isValidActivityEpochMs(nowEpochMs) || !currentOwner(customerAccountId)) return { status: "unavailable" };
      const generation = generations.get(customerAccountId) ?? 0;
      const publications: FrontendSingleAppointmentPublicationV1[] = [];
      for (const { publication: row, accepted: originalAccepted } of rows.values()) {
        if (row.customerAccountId !== customerAccountId) continue;
        if (row.expiresAtEpochMs <= nowEpochMs || row.item.freshness.staleAtEpochMs <= nowEpochMs) continue;
        const accepted = capture(options.readAccepted(customerAccountId, row.idempotencyKey));
        const current = validateFrontendSingleAppointmentPublicationV1(row, accepted, customerAccountId, nowEpochMs);
        if (!current || !samePackageContractData(accepted, originalAccepted)) return { status: "unavailable" };
        publications.push(current);
      }
      if (!currentOwner(customerAccountId) || mutation !== mutationVersion || !customerOwnedSessionStorageMutationProofIsCurrent(proof)) return { status: "unavailable" };
      const snapshot = freeze({ status: "ready" as const, customerAccountId, generation, publications });
      readVersions.set(snapshot, { source, version: mutationVersion });
      return snapshot;
    } catch { return { status: "unavailable" }; }
    finally { reading = false; }
  };
  const source: FrontendSingleAppointmentPublicationSource = Object.freeze({ read, subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; } });
  registeredSources.set(source, read);
  sourceVersions.set(source, mutationVersion);
  return Object.freeze({
    source,
    publish(input: unknown, expectedGeneration: number, isCurrentOwner: () => boolean): boolean {
      if (writing) return false;
      writing = true;
      const mutation = mutationVersion;
      const proof = captureCustomerOwnedSessionStorageMutationProof();
      try {
        const value = capture(input);
        if (!record(value) || !isActivityIdentifier(value.customerAccountId) || typeof value.idempotencyKey !== "string" ||
          !Number.isSafeInteger(expectedGeneration) || expectedGeneration < 0 || !isCurrentOwner()) return false;
        const customer = value.customerAccountId;
        const generation = generations.get(customer) ?? 0;
        if (generation !== expectedGeneration || !currentOwner(customer)) return false;
        const accepted = capture(options.readAccepted(customer, value.idempotencyKey));
        const publication = validateFrontendSingleAppointmentPublicationV1(value, accepted, customer, now());
        if (!publication) return false;
        const key = keyFor(customer, publication.providerBookingId);
        const existing = rows.get(key);
        const identityKeys = [`booking:${publication.providerBookingId}`, `receipt:${publication.providerReceiptId}`, `submission:${publication.idempotencyKey}`];
        if (identityKeys.some(identity => {
          const previous = acceptedIdentities.get(identity);
          return previous && (previous.customer !== customer || !samePackageContractData(previous.accepted, accepted));
        })) return false;
        if (existing && !samePackageContractData(existing.accepted, accepted)) return false;
        if ([...rows.values()].some(row => row !== existing && (row.publication.providerBookingId === publication.providerBookingId ||
          row.publication.providerReceiptId === publication.providerReceiptId || row.publication.idempotencyKey === publication.idempotencyKey))) return false;
        // Repeat the ledger after arbitrary owner/clock callbacks; the final
        // comparison and mutation below invoke no external callback.
        if (!isCurrentOwner() || !currentOwner(customer)) return false;
        const finalAccepted = capture(options.readAccepted(customer, publication.idempotencyKey));
        if (!samePackageContractData(accepted, finalAccepted) || mutation !== mutationVersion ||
          !customerOwnedSessionStorageMutationProofIsCurrent(proof)) return false;
        if (existing?.publication.publicationRevision === publication.publicationRevision) return samePackageContractData(existing.publication, publication);
        const revisionKey = JSON.stringify([customer, publication.providerBookingId, publication.publicationRevision]);
        if (usedRevisions.has(revisionKey) || usedRevisions.size >= 512 || !existing && rows.size >= 64 ||
          existing && (existing.publication.publishedAtEpochMs > publication.publishedAtEpochMs ||
            existing.publication.item.revision.entityRevision === publication.item.revision.entityRevision ||
            existing.publication.item.revision.generatedAtEpochMs > publication.item.revision.generatedAtEpochMs)) return false;
        const frozenAccepted = freeze(accepted);
        rows.set(key, { publication, accepted: frozenAccepted });
        for (const identity of identityKeys) acceptedIdentities.set(identity, { customer, accepted: frozenAccepted });
        usedRevisions.add(revisionKey);
        changed(customer);
        return true;
      } catch { return false; }
      finally { writing = false; }
    },
    withdraw(customer: string, booking: string, expectedGeneration: number, isCurrentOwner: () => boolean): boolean {
      if (writing) return false;
      writing = true;
      const mutation = mutationVersion;
      const proof = captureCustomerOwnedSessionStorageMutationProof();
      try {
        if (!isActivityIdentifier(customer) || !isActivityIdentifier(booking) || !Number.isSafeInteger(expectedGeneration) ||
          expectedGeneration !== (generations.get(customer) ?? 0) || !rows.has(keyFor(customer, booking)) ||
          !isCurrentOwner() || !currentOwner(customer) || mutation !== mutationVersion || !customerOwnedSessionStorageMutationProofIsCurrent(proof)) return false;
        rows.delete(keyFor(customer, booking));
        changed(customer);
        return true;
      } catch { return false; }
      finally { writing = false; }
    },
  });
}
